import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    PlayCircle, Trophy, Warning, ChartLineUp, Star, Shuffle, TrendUp, TrendDown, Info, Sparkle,
    Play, Pause, FastForward, ArrowsLeftRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceLine, Legend,
} from "recharts";
import { timingPoolSimulate } from "../../lib/timing-pool-api";
import { track } from "../../lib/analytics";
import axios from "axios";
import ManagerPhasePanel from "./ManagerPhasePanel";

const MANAGER_API = `${process.env.REACT_APP_BACKEND_URL}/api/timing/manager`;
const MANAGER_LS = "timing.manager.session";

// ---------------------------------------------------------------------------
// Season Simulator — reads the shared pool, POSTs to /pool/simulate,
// renders cumulative points, per-GW table with XI/bench, chip timeline,
// rank estimator.
// ---------------------------------------------------------------------------

const CHIP_LABELS = {
    triple_captain_1: { label: "TC 1", color: "text-fa-yellow border-fa-yellow/50 bg-fa-yellow/10" },
    triple_captain_2: { label: "TC 2", color: "text-fa-yellow border-fa-yellow/50 bg-fa-yellow/10" },
    bench_boost_1:    { label: "BB 1", color: "text-fa-mint border-fa-mint/50 bg-fa-mint/10" },
    bench_boost_2:    { label: "BB 2", color: "text-fa-mint border-fa-mint/50 bg-fa-mint/10" },
    wildcard_1:       { label: "WC 1", color: "text-fa-sky border-fa-sky/50 bg-fa-sky/10" },
    wildcard_2:       { label: "WC 2", color: "text-fa-sky border-fa-sky/50 bg-fa-sky/10" },
    free_hit_1:       { label: "FH 1", color: "text-pink-300 border-pink-400/50 bg-pink-500/10" },
    free_hit_2:       { label: "FH 2", color: "text-pink-300 border-pink-400/50 bg-pink-500/10" },
    // Legacy fallbacks (v starých simulacích byly bez _1/_2)
    triple_captain: { label: "Triple Captain", color: "text-fa-yellow border-fa-yellow/50 bg-fa-yellow/10" },
    bench_boost:    { label: "Bench Boost",    color: "text-fa-mint border-fa-mint/50 bg-fa-mint/10" },
    free_hit:       { label: "Free Hit",       color: "text-pink-300 border-pink-400/50 bg-pink-500/10" },
};

const CHIP_SHORT = (chipKey) => (CHIP_LABELS[chipKey]?.label) || chipKey;

const fmtRank = (n) => (n == null ? "—" : n.toLocaleString("cs-CZ").replace(/\u00a0/g, " "));

const scriptLabel = (s) => {
    const a = s.archetype;
    if (a === "replay") return `kopíruje ročník ${s.detail}`;
    if (a === "strong") return `silný ročník (${s.detail})`;
    if (a === "weak") return `slabý ročník (${s.detail})`;
    if (a === "unknown") return "málo dat · náhodná forma";
    return "standardní forma";
};

function ScriptsPanel({ scripts }) {
    const [open, setOpen] = useState(false);
    const sorted = [...scripts].sort((a, b) => (b.season_mult || 1) - (a.season_mult || 1));
    return (
        <div className="border border-white/10 bg-[#0d0d0d]" data-testid="sim-scripts-panel">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                data-testid="sim-scripts-toggle"
                className="w-full flex items-center gap-2 p-3 text-left hover:bg-white/[0.03]"
            >
                <Sparkle size={12} weight="fill" className="text-fa-yellow" />
                <span className="font-display font-black text-sm uppercase tracking-widest">
                    Skrytý scénář sezóny
                </span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                    náhodně vylosováno před GW1 — AI ho nezná
                </span>
                <span className="ml-auto font-mono text-[10px] text-fa-sky">{open ? "skrýt" : `zobrazit (${scripts.length})`}</span>
            </button>
            {open && (
                <div className="p-3 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                    {sorted.map((s) => {
                        const mult = s.season_mult || 1;
                        const color = mult > 1.05 ? "text-fa-mint" : mult < 0.95 ? "text-red-300" : "text-white/60";
                        return (
                            <div key={s.fpl_id} className="border border-white/[0.07] px-2 py-1.5 flex items-center gap-2"
                                 data-testid={`sim-script-${s.fpl_id}`}>
                                <span className="font-display font-bold text-xs truncate">{s.name}</span>
                                <span className="font-mono text-[8px] uppercase text-white/35">{s.position}</span>
                                <span className={`ml-auto font-mono text-[9px] whitespace-nowrap ${color}`}>
                                    {scriptLabel(s)} ×{mult.toFixed(2)}
                                </span>
                                {s.start_form === "cold" && (
                                    <span className="font-mono text-[8px] uppercase px-1 py-0.5 bg-sky-900/40 text-sky-300" title="GW1-8 ×0.65">studený start</span>
                                )}
                                {s.start_form === "hot" && (
                                    <span className="font-mono text-[8px] uppercase px-1 py-0.5 bg-orange-900/40 text-orange-300" title="GW1-8 ×1.3">raketový start</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function StatPill({ label, value, sub, accent = "text-white" }) {
    return (
        <div className="border border-white/10 bg-[#0d0d0d] px-3 py-2 min-w-[110px]">
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">{label}</div>
            <div className={`font-display font-black text-2xl tabular-nums ${accent}`}>{value}</div>
            {sub && <div className="font-mono text-[9px] text-white/55 mt-0.5">{sub}</div>}
        </div>
    );
}

function ChipTimeline({ chips }) {
    if (!chips?.length) {
        return (
            <div className="font-mono text-[10px] text-white/40 italic border border-white/10 bg-[#0d0d0d] p-3">
                AI nepoužila žádný chip
            </div>
        );
    }
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="sim-chips">
            <div className="flex items-center gap-2 mb-2">
                <Star size={12} weight="fill" className="text-fa-yellow" />
                <h4 className="font-display font-black text-xs uppercase tracking-widest">Použité chipy</h4>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {chips.map((c, i) => {
                    const meta = CHIP_LABELS[c.chip] || { label: c.chip, color: "text-white border-white/30" };
                    return (
                        <span
                            key={`${c.gw}-${c.chip}-${i}`}
                            className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${meta.color}`}
                            data-testid={`sim-chip-${c.chip}`}
                        >
                            GW{c.gw} · {meta.label}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// TransfersPanel — collapsible list of every AI-made transfer, grouped by GW.
// Respects the live reveal (only shows transfers up to `revealedGw`).
// ---------------------------------------------------------------------------
function TransfersPanel({ transferHistory, revealedGw }) {
    const [open, setOpen] = useState(true);
    if (!transferHistory?.length) return null;
    const visible = transferHistory.filter((t) => t.gw <= revealedGw);
    const totalTransfers = visible.reduce((n, t) => n + (t.transfers?.length || 0), 0);
    return (
        <div className="border border-white/10 bg-[#0d0d0d]" data-testid="sim-transfers-panel">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 p-3 text-left hover:bg-white/[0.03]"
                data-testid="sim-transfers-toggle"
            >
                <ArrowsLeftRight size={12} weight="fill" className="text-fa-mint" />
                <span className="font-display font-black text-sm uppercase tracking-widest">
                    Přestupy AI ({totalTransfers})
                </span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                    manažer řeší přestupy podle projekce · 1 FT/GW · banka 5
                </span>
                <span className="ml-auto font-mono text-[10px] text-fa-sky">{open ? "skrýt" : "zobrazit"}</span>
            </button>
            {open && (
                <div className="p-3 pt-0">
                    {visible.length === 0 ? (
                        <div className="font-mono text-[10px] text-white/40 py-2">Zatím žádné přestupy…</div>
                    ) : (
                        <ul className="space-y-1">
                            {visible.map((entry) => (
                                <li key={entry.gw} className="flex items-start gap-2 border border-white/[0.06] bg-black/40 px-2 py-1.5"
                                    data-testid={`sim-transfer-gw-${entry.gw}`}>
                                    <span className="font-display font-black text-xs text-fa-mint tabular-nums shrink-0 w-10">
                                        GW{entry.gw}
                                    </span>
                                    <div className="flex flex-wrap gap-1.5 flex-1">
                                        {(entry.transfers || []).map((t, i) => (
                                            <span key={i} className="font-mono text-[10px] border border-fa-mint/25 bg-fa-mint/5 px-1.5 py-0.5">
                                                <span className="text-red-300">{t.out}</span>
                                                <span className="text-white/40 mx-1">→</span>
                                                <span className="text-fa-mint">{t.in}</span>
                                                {t.gain != null && (
                                                    <span className="text-white/40 ml-1">(+{t.gain})</span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// OverallRankChart — line chart of the manager's Overall Rank across GWs,
// with chip markers overlaid. Y-axis is INVERTED (best rank at top).
// ---------------------------------------------------------------------------
function ChipDot(props) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    if (!payload?.chip_label) {
        return <circle cx={cx} cy={cy} r={2} fill="#9EE8B4" />;
    }
    return (
        <g>
            <circle cx={cx} cy={cy} r={4} fill="#FFF3C2" stroke="#0d0d0d" strokeWidth={1} />
            <text
                x={cx}
                y={cy - 10}
                fontSize={9}
                fontFamily="IBM Plex Mono, monospace"
                textAnchor="middle"
                fill="#FFF3C2"
                style={{ textTransform: "uppercase", letterSpacing: 1 }}
            >
                {payload.chip_label}
            </text>
        </g>
    );
}

function OverallRankChart({ cumulative, chipHistory, revealedGw, finalOR }) {
    const visible = (cumulative || []).slice(0, revealedGw);
    if (visible.length === 0) return null;
    const chipByGw = {};
    for (const c of chipHistory || []) {
        chipByGw[c.gw] = (chipByGw[c.gw] || []).concat([c.chip]);
    }
    const data = visible.map((d) => ({ ...d, chip_label: chipByGw[d.gw] ? chipByGw[d.gw].map(CHIP_SHORT).join(" · ") : null }));
    // Compute y domain — invert so rank 1 is at top.
    const maxOR = Math.max(...visible.map((d) => d.overall_rank || 0), 1);
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="sim-or-chart">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <ChartLineUp size={12} weight="fill" className="text-fa-mint" />
                <h3 className="font-display font-black text-sm uppercase tracking-widest">
                    Overall Rank (živě)
                </h3>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                    ~10,5M FPL manažerů · nižší = lepší
                </span>
                {finalOR != null && revealedGw >= 38 && (
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-fa-mint/40 text-fa-mint">
                        Konečný OR: <strong className="tabular-nums">{fmtRank(finalOR)}</strong>
                    </span>
                )}
            </div>
            <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis
                            dataKey="gw"
                            stroke="rgba(255,255,255,0.4)"
                            tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                            type="number"
                            domain={[1, 38]}
                            allowDecimals={false}
                        />
                        <YAxis
                            stroke="rgba(255,255,255,0.4)"
                            tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                            reversed
                            domain={[1, Math.max(maxOR, 8_000_000)]}
                            tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v / 1000)}k` : v}
                        />
                        <Tooltip
                            wrapperStyle={{ outline: "none" }}
                            contentStyle={{
                                background: "#0d0d0d",
                                border: "1px solid rgba(255,255,255,0.15)",
                                fontFamily: "IBM Plex Mono, monospace",
                                fontSize: 11,
                                color: "#fff",
                            }}
                            labelStyle={{ color: "#9EE8B4", fontWeight: 700 }}
                            labelFormatter={(gw) => `GW ${gw}`}
                            formatter={(val, name, ctx) => {
                                if (name === "Overall Rank") return [fmtRank(val), name];
                                return [val, name];
                            }}
                        />
                        <Line
                            type="monotone"
                            dataKey="overall_rank"
                            name="Overall Rank"
                            stroke="#9EE8B4"
                            strokeWidth={2.5}
                            dot={ChipDot}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function GwRow({ r, expanded, onToggle }) {
    const chipMeta = r.chip ? CHIP_LABELS[r.chip] : null;
    const diff = r.overall_rank_diff;
    const diffPct = r.overall_rank_diff_pct;
    const rankImprovedIcon = diff == null ? null : diff < 0
        ? <TrendUp size={11} weight="fill" className="text-fa-mint" />
        : diff > 0
            ? <TrendDown size={11} weight="fill" className="text-red-300" />
            : null;
    return (
        <>
            <tr
                onClick={() => onToggle(r.gw)}
                className={`border-b border-white/[0.05] cursor-pointer hover:bg-white/[0.03] ${expanded ? "bg-white/[0.03]" : ""}`}
                data-testid={`sim-gw-${r.gw}`}
            >
                <td className="px-2 py-1.5 font-display font-black tabular-nums">GW{r.gw}</td>
                <td className="px-2 py-1.5 tabular-nums text-white/85">{fmtRank(r.overall_rank)}</td>
                <td className="px-2 py-1.5 tabular-nums text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                        {rankImprovedIcon}
                        <span className={diff == null ? "text-white/40" : diff < 0 ? "text-fa-mint" : diff > 0 ? "text-red-300" : "text-white/60"}>
                            {diff == null ? "—" : `${diff > 0 ? "+" : ""}${fmtRank(diff)}`}
                        </span>
                    </span>
                </td>
                <td className="px-2 py-1.5 tabular-nums text-right">
                    <span className={diffPct == null ? "text-white/40" : diffPct < 0 ? "text-fa-mint" : diffPct > 0 ? "text-red-300" : "text-white/60"}>
                        {diffPct == null ? "—" : `${diffPct > 0 ? "+" : ""}${diffPct}%`}
                    </span>
                </td>
                <td className="px-2 py-1.5 tabular-nums font-bold text-fa-yellow text-right">{r.points}</td>
                <td className="px-2 py-1.5 tabular-nums text-right text-white/75">{fmtRank(r.gw_rank)}</td>
                <td className="px-2 py-1.5 truncate">
                    {r.captain.name}
                    <span className="text-white/40 ml-1">({r.captain.cap_mult}×)</span>
                </td>
                <td className="px-2 py-1.5">
                    {chipMeta && (
                        <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${chipMeta.color}`}
                              data-testid={`sim-gw-${r.gw}-chip`}>
                            {chipMeta.label}
                        </span>
                    )}
                    {r.wildcard_activated && (
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border text-fa-sky border-fa-sky/50 bg-fa-sky/10 ml-1">
                            {CHIP_SHORT(r.chip) || "WC"}
                        </span>
                    )}
                    {r.transfers?.length > 0 && (
                        <span
                            className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border text-fa-mint border-fa-mint/50 bg-fa-mint/10 ml-1"
                            title={r.transfers.map((t) => `${t.out} → ${t.in}`).join("\n")}
                            data-testid={`sim-gw-${r.gw}-transfers`}
                        >
                            ⇄ {r.transfers.length}
                        </span>
                    )}
                </td>
                <td className="px-2 py-1.5 text-right text-white/40">{expanded ? "▲" : "▼"}</td>
            </tr>
            {expanded && (
                <tr className="border-b border-white/[0.05] bg-black/40">
                    <td colSpan={9} className="px-3 py-2">
                        {r.transfers?.length > 0 && (
                            <div className="mb-2 flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">Přestupy:</span>
                                {r.transfers.map((t, i) => (
                                    <span key={i} className="font-mono text-[10px] border border-fa-mint/30 bg-fa-mint/5 px-1.5 py-0.5">
                                        <span className="text-red-300">{t.out}</span>
                                        <span className="text-white/40 mx-1">→</span>
                                        <span className="text-fa-mint">{t.in}</span>
                                        <span className="text-white/35 ml-1">(+{t.gain})</span>
                                    </span>
                                ))}
                            </div>
                        )}
                        {r.autosubs?.length > 0 && (
                            <div className="mb-2 flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Autosuby:</span>
                                {r.autosubs.map((a, i) => (
                                    <span key={i} className="font-mono text-[10px] border border-white/15 bg-white/[0.03] px-1.5 py-0.5">
                                        <span className="text-white/45">{a.out} (0 b)</span>
                                        <span className="text-white/40 mx-1">⟳</span>
                                        <span className="text-white">{a.in} ({a.in_points} b)</span>
                                    </span>
                                ))}
                                {r.captain?.vice_used && (
                                    <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 bg-fa-yellow/10 text-fa-yellow border border-fa-yellow/30">
                                        kapitán blankoval → páska na vice
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            <div>
                                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint mb-1">Základní XI</div>
                                <ul className="space-y-0.5">
                                    {r.xi.map((p) => (
                                        <li key={p.fpl_id} className="flex items-center justify-between font-mono text-[10px]">
                                            <span className="flex items-center gap-1.5 min-w-0">
                                                <span className="text-white/40 w-8 text-[9px]">{p.position}</span>
                                                <span className={`font-display font-black text-[12px] truncate ${p.fpl_id === r.captain.fpl_id ? "text-fa-yellow" : ""}`}>
                                                    {p.name}{p.fpl_id === r.captain.fpl_id ? " (C)" : ""}
                                                </span>
                                                {p.opponent && (
                                                    <span className="text-white/35 text-[9px]">
                                                        vs {p.opponent} ({p.is_home ? "H" : "A"})
                                                    </span>
                                                )}
                                            </span>
                                            <span className="tabular-nums text-white/70">{p.points}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Lavička</div>
                                <ul className="space-y-0.5">
                                    {r.bench.map((p) => (
                                        <li key={p.fpl_id} className="flex items-center justify-between font-mono text-[10px]">
                                            <span className="flex items-center gap-1.5 min-w-0">
                                                <span className="text-white/40 w-8 text-[9px]">{p.position}</span>
                                                <span className="font-display font-black text-[12px] text-white/60 truncate">{p.name}</span>
                                                {p.opponent && (
                                                    <span className="text-white/35 text-[9px]">
                                                        vs {p.opponent} ({p.is_home ? "H" : "A"})
                                                    </span>
                                                )}
                                            </span>
                                            <span className="tabular-nums text-white/50">{p.points}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

// ---------------------------------------------------------------------------
// Manual squad picker — click chips from the 30-pool to build the 15-man team.
// Enforces 2/5/5/3 quota; supports captain selection.
// ---------------------------------------------------------------------------
const MANUAL_QUOTA = { GKP: 2, DEF: 5, MID: 5, FWD: 3 };
const MAX_PER_TEAM = 3;
const CUSTOM30_QUOTA = { GKP: 4, DEF: 10, MID: 10, FWD: 6 };
const POOL_MAX_LABEL = 100;

function teamCounts(pool, ids) {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    const c = {};
    for (const p of pool) {
        if (idSet.has(p.fpl_id)) c[p.team || "?"] = (c[p.team || "?"] || 0) + 1;
    }
    return c;
}

function ManualSquadPicker({ pool, squadIds, setSquadIds, captainId, setCaptainId, disableChips, setDisableChips, disableWildcard, setDisableWildcard }) {
    const idSet = new Set(squadIds);
    const counts = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
    for (const p of pool) if (idSet.has(p.fpl_id) && counts[p.position] !== undefined) counts[p.position] += 1;
    const total = squadIds.length;
    const isFull = total === 15
        && counts.GKP === MANUAL_QUOTA.GKP && counts.DEF === MANUAL_QUOTA.DEF
        && counts.MID === MANUAL_QUOTA.MID && counts.FWD === MANUAL_QUOTA.FWD;

    // Live cost / budget indicator — mirrored inline in the header pill.
    const cost = useMemo(() => {
        return pool.reduce((a, p) => a + (idSet.has(p.fpl_id) ? (p.current_price ?? 4.5) : 0), 0);
    }, [pool, squadIds.join(",")]);
    const overBudget = cost > 100.0 + 0.05;
    const budgetLeft = 100.0 - cost;

    const togglePlayer = (p) => {
        if (idSet.has(p.fpl_id)) {
            setSquadIds(squadIds.filter((id) => id !== p.fpl_id));
            if (captainId === p.fpl_id) setCaptainId(null);
        } else {
            const posCount = counts[p.position] || 0;
            if (posCount >= MANUAL_QUOTA[p.position]) {
                toast.error(`Max ${MANUAL_QUOTA[p.position]} ${p.position}`);
                return;
            }
            const tCounts = teamCounts(pool, idSet);
            if ((tCounts[p.team || "?"] || 0) >= MAX_PER_TEAM) {
                toast.error(`Max ${MAX_PER_TEAM} hráči z jednoho klubu (${p.team})`);
                return;
            }
            setSquadIds([...squadIds, p.fpl_id]);
        }
    };

    const autofillBySeasonDna = () => {
        // Greedy by price desc per position (scarcest position first) honouring
        // max-3-per-club; then a fix-up pass swaps out capped-club picks so the
        // squad always completes when the pool allows it.
        const byPos = { GKP: [], DEF: [], MID: [], FWD: [] };
        for (const p of pool) if (byPos[p.position]) byPos[p.position].push(p);
        for (const k in byPos) byPos[k].sort((a, b) => (b.current_price ?? 4.5) - (a.current_price ?? 4.5));
        const order = Object.entries(MANUAL_QUOTA)
            .sort(([a], [b]) => (byPos[a].length - MANUAL_QUOTA[a]) - (byPos[b].length - MANUAL_QUOTA[b]));
        const picked = new Map(); // fpl_id -> player
        const tCounts = {};
        const take = (p) => { picked.set(p.fpl_id, p); tCounts[p.team || "?"] = (tCounts[p.team || "?"] || 0) + 1; };
        const drop = (p) => { picked.delete(p.fpl_id); tCounts[p.team || "?"] -= 1; };
        const missing = [];
        for (const [pos, n] of order) {
            let taken = 0;
            for (const p of byPos[pos]) {
                if (taken >= n) break;
                if ((tCounts[p.team || "?"] || 0) >= MAX_PER_TEAM) continue;
                take(p); taken += 1;
            }
            for (let i = taken; i < n; i++) missing.push(pos);
        }
        // Fix-up: for each unfilled slot, free a capped club by swapping one of
        // its picked players for a same-position alternative from another club.
        for (const pos of [...missing]) {
            const cand = byPos[pos].find((p) => !picked.has(p.fpl_id) && (tCounts[p.team || "?"] || 0) >= MAX_PER_TEAM);
            if (!cand) continue;
            const club = cand.team || "?";
            let swapped = false;
            for (const q of [...picked.values()]) {
                if ((q.team || "?") !== club) continue;
                const alt = byPos[q.position].find((r) =>
                    !picked.has(r.fpl_id) && r.fpl_id !== cand.fpl_id
                    && (tCounts[r.team || "?"] || 0) < MAX_PER_TEAM);
                if (alt) {
                    drop(q); take(alt); take(cand);
                    missing.splice(missing.indexOf(pos), 1);
                    swapped = true;
                    break;
                }
            }
            if (swapped) continue;
        }
        if (missing.length > 0) {
            const need = missing.reduce((m, p) => ({ ...m, [p]: (m[p] || 0) + 1 }), {});
            toast.warning(`Autofill nedokázal doplnit: ${Object.entries(need).map(([p, n]) => `${n}× ${p}`).join(", ")} (limit 3 z klubu) — přidej do poolu hráče z jiných klubů nebo dober ručně.`);
        }
        setSquadIds([...picked.keys()]);
    };

    const clearSquad = () => { setSquadIds([]); setCaptainId(null); };

    // Group pool by position for a clean picker UI
    const byPos = { GKP: [], DEF: [], MID: [], FWD: [] };
    for (const p of pool) if (byPos[p.position]) byPos[p.position].push(p);
    for (const k in byPos) byPos[k].sort((a, b) => b.current_price - a.current_price);

    return (
        <div className="border border-fa-mint/30 bg-fa-mint/[0.02] p-3 space-y-3" data-testid="manual-picker">
            <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-fa-mint font-bold">
                    Ruční výběr sestavy
                </span>
                <span className="font-mono text-[10px] text-white/50 tabular-nums">
                    {total}/15 vybráno
                </span>
                {["GKP", "DEF", "MID", "FWD"].map((pos) => {
                    const c = counts[pos], t = MANUAL_QUOTA[pos];
                    const clr = c === t ? "text-fa-mint" : c > t ? "text-red-300" : "text-fa-yellow";
                    return (
                        <span key={pos} className={`font-mono text-[10px] tabular-nums ${clr}`}>
                            {pos} {c}/{t}
                        </span>
                    );
                })}
                {isFull && (
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-fa-mint/50 text-fa-mint bg-fa-mint/10 font-bold">
                        ✓ Kompletní
                    </span>
                )}
                <span className={`font-mono text-[11px] uppercase tracking-widest px-2 py-1 border font-bold tabular-nums ${
                    overBudget
                        ? "border-red-400/50 text-red-300 bg-red-500/10"
                        : total === 15
                            ? "border-fa-mint/50 text-fa-mint bg-fa-mint/10"
                            : "border-fa-yellow/40 text-fa-yellow bg-fa-yellow/5"
                }`} data-testid="manual-cost-header">
                    £{cost.toFixed(1)}m / £100.0m
                    <span className="ml-2 text-white/50">(zbývá £{budgetLeft.toFixed(1)}m)</span>
                </span>
                <button
                    type="button"
                    onClick={autofillBySeasonDna}
                    data-testid="manual-autofill"
                    className="ml-auto font-mono text-[9px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1 hover:bg-fa-sky hover:text-black"
                >
                    Autofill (dle ceny)
                </button>
                <button
                    type="button"
                    onClick={clearSquad}
                    data-testid="manual-clear"
                    className="font-mono text-[9px] uppercase tracking-widest text-red-300 border border-red-300/40 px-2 py-1 hover:bg-red-300 hover:text-black"
                >
                    Vyprázdnit
                </button>
            </div>

            {/* Player chips grouped by position */}
            <div className="space-y-2">
                {["GKP", "DEF", "MID", "FWD"].map((pos) => (
                    <div key={pos}>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">
                            {pos} · potřeba {MANUAL_QUOTA[pos]}
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {byPos[pos].map((p) => {
                                const inSquad = idSet.has(p.fpl_id);
                                const isCap = captainId === p.fpl_id;
                                return (
                                    <button
                                        key={p.fpl_id}
                                        type="button"
                                        onClick={() => togglePlayer(p)}
                                        data-testid={`manual-pick-${p.fpl_id}`}
                                        className={`inline-flex items-center gap-1 px-2 py-1 border text-[11px] font-mono ${
                                            inSquad
                                                ? isCap
                                                    ? "bg-fa-yellow text-black border-fa-yellow font-bold"
                                                    : "bg-fa-mint/20 text-fa-mint border-fa-mint/60"
                                                : "bg-white/[0.02] text-white/60 border-white/15 hover:border-white/40"
                                        }`}
                                    >
                                        <span className="font-display font-black">{p.display_name}</span>
                                        <span className="text-[9px] opacity-70">{p.current_price == null ? "£—" : `£${p.current_price.toFixed(1)}`}</span>
                                        {inSquad && (
                                            <span
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCaptainId(isCap ? null : p.fpl_id);
                                                }}
                                                data-testid={`manual-cap-${p.fpl_id}`}
                                                title={isCap ? "Zrušit kapitánství" : "Označit jako kapitána"}
                                                className={`ml-1 px-1 text-[8px] uppercase tracking-widest border ${isCap ? "border-black/40 text-black" : "border-fa-yellow/60 text-fa-yellow hover:bg-fa-yellow/20"}`}
                                            >
                                                {isCap ? "C ✓" : "C"}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Manual-mode extras */}
            <div className="flex items-center gap-3 flex-wrap font-mono text-[10px] pt-2 border-t border-white/[0.06]">
                <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={disableChips}
                        onChange={(e) => setDisableChips(e.target.checked)}
                        data-testid="manual-disable-chips"
                        className="accent-fa-yellow"
                    />
                    <span className="uppercase tracking-widest text-white/60">Bez chipů (TC/BB)</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={disableWildcard}
                        onChange={(e) => setDisableWildcard(e.target.checked)}
                        data-testid="manual-disable-wildcard"
                        className="accent-fa-yellow"
                    />
                    <span className="uppercase tracking-widest text-white/60">Zamknout sestavu (bez wildcardu)</span>
                </label>
                {captainId && (
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                        Kapitán: <strong>{pool.find((p) => p.fpl_id === captainId)?.display_name}</strong>
                    </span>
                )}
            </div>
        </div>
    );
}

// Custom 30-pool picker — pick exactly 4/10/10/6 from the watchlist for the AI sim.
function Custom30Picker({ pool, ids, setIds }) {
    const idSet = new Set(ids);
    const counts = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
    for (const p of pool) if (idSet.has(p.fpl_id) && counts[p.position] !== undefined) counts[p.position] += 1;

    const toggle = (p) => {
        if (idSet.has(p.fpl_id)) {
            setIds(ids.filter((x) => x !== p.fpl_id));
        } else {
            if (counts[p.position] >= CUSTOM30_QUOTA[p.position]) {
                toast.error(`${p.position} už je plné (${CUSTOM30_QUOTA[p.position]})`);
                return;
            }
            setIds([...ids, p.fpl_id]);
        }
    };

    const autofill = () => {
        const byPos = { GKP: [], DEF: [], MID: [], FWD: [] };
        for (const p of pool) if (byPos[p.position]) byPos[p.position].push(p);
        const next = [];
        for (const [pos, n] of Object.entries(CUSTOM30_QUOTA)) {
            byPos[pos].sort((a, b) => (b.current_price ?? 0) - (a.current_price ?? 0));
            next.push(...byPos[pos].slice(0, n).map((p) => p.fpl_id));
        }
        setIds(next);
    };

    const byPos = { GKP: [], DEF: [], MID: [], FWD: [] };
    for (const p of pool) if (byPos[p.position]) byPos[p.position].push(p);

    return (
        <div className="border border-fa-sky/30 bg-fa-sky/[0.02] p-3 space-y-3" data-testid="custom30-picker">
            <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-fa-sky font-bold">
                    Vlastních 30 pro simulaci
                </span>
                {Object.entries(CUSTOM30_QUOTA).map(([pos, t]) => {
                    const c = counts[pos];
                    const clr = c === t ? "text-fa-mint" : c > t ? "text-red-300" : "text-fa-yellow";
                    return (
                        <span key={pos} className={`font-mono text-[10px] tabular-nums ${clr}`} data-testid={`custom30-count-${pos}`}>
                            {pos} {c}/{t}
                        </span>
                    );
                })}
                <span className={`font-mono text-[10px] tabular-nums font-bold ${ids.length === 30 ? "text-fa-mint" : "text-white/60"}`}>
                    Σ {ids.length}/30
                </span>
                <span className="ml-auto flex gap-1.5">
                    <button
                        type="button"
                        onClick={autofill}
                        data-testid="custom30-autofill"
                        className="font-mono text-[9px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1 hover:bg-fa-sky hover:text-black"
                    >
                        Vyplnit dle ceny
                    </button>
                    <button
                        type="button"
                        onClick={() => setIds([])}
                        data-testid="custom30-clear"
                        className="font-mono text-[9px] uppercase tracking-widest text-red-300 border border-red-300/40 px-2 py-1 hover:bg-red-300 hover:text-black"
                    >
                        Vyčistit
                    </button>
                </span>
            </div>
            {["GKP", "DEF", "MID", "FWD"].map((pos) => (
                <div key={pos} className="space-y-1">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{pos} · vybráno {counts[pos]}/{CUSTOM30_QUOTA[pos]}</div>
                    <div className="flex gap-1 flex-wrap">
                        {byPos[pos].map((p) => {
                            const sel = idSet.has(p.fpl_id);
                            return (
                                <button
                                    key={p.fpl_id}
                                    type="button"
                                    onClick={() => toggle(p)}
                                    data-testid={`custom30-pick-${p.fpl_id}`}
                                    className={`font-mono text-[10px] px-1.5 py-0.5 border transition-colors ${sel
                                        ? "bg-fa-sky text-black border-fa-sky font-bold"
                                        : "border-white/15 text-white/60 hover:text-white hover:border-white/40"}`}
                                >
                                    {p.display_name}
                                    <span className={`ml-1 text-[8px] ${sel ? "text-black/60" : "text-white/35"}`}>£{p.current_price ?? "?"}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function SeasonSimulator({ lang, sharedPool }) {
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);
    const [seed, setSeed] = useState(42);
    const [managerReport, setManagerReport] = useState(null);
    const [managerBusy, setManagerBusy] = useState(false);

    useEffect(() => {
        const sid = localStorage.getItem(MANAGER_LS);
        if (!sid) return;
        axios.get(`${MANAGER_API}/${sid}`)
            .then((r) => { setManagerReport(r.data); setMode("manager"); })
            .catch(() => localStorage.removeItem(MANAGER_LS));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const managerPayload = () => pool.map((p) => ({
        fpl_id: p.fpl_id, historical_slug: p.historical_slug, display_name: p.display_name,
        position: p.position, team: p.team, current_price: p.current_price,
    }));

    const startManager = async () => {
        if (!canRun) { toast.error("Sestava musí být kompletní 2/5/5/3 do £100m"); return; }
        setManagerBusy(true);
        try {
            const r = await axios.post(`${MANAGER_API}/start`, {
                players: managerPayload(), squad_ids: manualSquadIds,
                captain_id: manualCaptainId || null, seed,
            });
            setManagerReport(r.data);
            localStorage.setItem(MANAGER_LS, r.data.session_id);
            track("manager_mode_start", { pool_size: pool.length, seed });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Start manažerské sezóny selhal");
        } finally {
            setManagerBusy(false);
        }
    };

    const managerNext = async (transfers, captainId, newPlayers = []) => {
        setManagerBusy(true);
        try {
            let newPlayersPayload = [];
            if (newPlayers.length > 0) {
                let matches = [];
                try {
                    const m = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/timing/pool/match`, {
                        items: newPlayers.map((p) => ({ fpl_id: p.fpl_id, query: p.full_name || p.display_name })),
                    });
                    matches = m.data?.matches || [];
                } catch { /* fall back to no historical link */ }
                newPlayersPayload = newPlayers.map((p, i) => ({
                    fpl_id: p.fpl_id,
                    historical_slug: matches[i]?.historical_slug || null,
                    display_name: p.display_name,
                    position: p.position,
                    team: p.team,
                    current_price: p.current_price,
                }));
            }
            const r = await axios.post(`${MANAGER_API}/${managerReport.session_id}/next`, {
                transfers, captain_id: captainId || null, new_players: newPlayersPayload,
            });
            setManagerReport(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Simulace fáze selhala");
        } finally {
            setManagerBusy(false);
        }
    };

    const managerReset = () => {
        setManagerReport(null);
        localStorage.removeItem(MANAGER_LS);
    };
    const [expandedGw, setExpandedGw] = useState(null);
    // Live reveal: reveals GW rows one-by-one after simulation completes.
    // `revealedGw` is the highest GW currently visible (0 → nothing; 38 → all).
    const [revealedGw, setRevealedGw] = useState(0);
    const [livePlaying, setLivePlaying] = useState(false);
    const [liveSpeed, setLiveSpeed] = useState(220); // ms per GW
    // Pool source: full candidate pool (up to 100) vs top-30 by projection vs hand-picked 30
    const [poolSource, setPoolSource] = useState("top30"); // "all" | "top30" | "custom30"
    const [custom30Ids, setCustom30Ids] = useState([]);
    // Manual mode state
    const [mode, setMode] = useState("auto"); // "auto" | "manual"
    const [manualSquadIds, setManualSquadIds] = useState([]);
    const [manualCaptainId, setManualCaptainId] = useState(null);
    const [disableChips, setDisableChips] = useState(false);
    const [disableWildcard, setDisableWildcard] = useState(true);  // manual usually keeps squad locked

    const { pool } = sharedPool;
    const positionCounts = useMemo(() => {
        const c = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
        for (const p of pool) if (c[p.position] !== undefined) c[p.position] += 1;
        return c;
    }, [pool]);
    const validQuotaAuto = positionCounts.GKP >= 2 && positionCounts.DEF >= 5
                        && positionCounts.MID >= 5 && positionCounts.FWD >= 3;
    const custom30Counts = useMemo(() => {
        const c = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
        const ids = new Set(custom30Ids);
        for (const p of pool) if (ids.has(p.fpl_id) && c[p.position] !== undefined) c[p.position] += 1;
        return c;
    }, [pool, custom30Ids]);
    const custom30Valid = custom30Ids.length === 30
        && custom30Counts.GKP === CUSTOM30_QUOTA.GKP && custom30Counts.DEF === CUSTOM30_QUOTA.DEF
        && custom30Counts.MID === CUSTOM30_QUOTA.MID && custom30Counts.FWD === CUSTOM30_QUOTA.FWD;
    const useCustom30 = mode === "auto" && poolSource === "custom30" && pool.length > 30;
    const manualCounts = useMemo(() => {
        const c = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
        const ids = new Set(manualSquadIds);
        for (const p of pool) if (ids.has(p.fpl_id) && c[p.position] !== undefined) c[p.position] += 1;
        return c;
    }, [pool, manualSquadIds]);
    const manualValid = manualSquadIds.length === 15
        && manualCounts.GKP === 2 && manualCounts.DEF === 5
        && manualCounts.MID === 5 && manualCounts.FWD === 3;
    const manualCost = useMemo(() => {
        const ids = new Set(manualSquadIds);
        return pool.reduce((a, p) => a + (ids.has(p.fpl_id) ? (p.current_price ?? 4.5) : 0), 0);
    }, [pool, manualSquadIds]);
    const manualOverBudget = manualCost > 100.0 + 0.05;
    const manualTeamViolations = useMemo(() => {
        const c = teamCounts(pool, manualSquadIds);
        return Object.entries(c).filter(([, n]) => n > MAX_PER_TEAM).map(([t]) => t);
    }, [pool, manualSquadIds]);
    const canRun = mode === "auto"
        ? (validQuotaAuto && (!useCustom30 || custom30Valid))
        : (manualValid && !manualOverBudget && manualTeamViolations.length === 0);

    const runSim = async () => {
        if (!canRun) {
            toast.error(mode === "auto"
                ? (useCustom30 && !custom30Valid
                    ? `Vlastní 30 musí být přesně 4 GKP · 10 DEF · 10 MID · 6 FWD (máš ${custom30Ids.length})`
                    : "Pool musí mít min. 2 GKP · 5 DEF · 5 MID · 3 FWD")
                : manualOverBudget
                    ? `Ruční sestava stojí £${manualCost.toFixed(1)}m — musí být do £100m`
                    : manualTeamViolations.length
                        ? `Max 3 hráči z jednoho klubu — překročeno: ${manualTeamViolations.join(", ")}`
                        : "Ruční sestava musí být kompletní 2/5/5/3");
            return;
        }
        setRunning(true);
        try {
            const custom30Set = new Set(custom30Ids);
            const simPool = useCustom30 ? pool.filter((p) => custom30Set.has(p.fpl_id)) : pool;
            const payload = simPool.map((p) => ({
                fpl_id: p.fpl_id,
                historical_slug: p.historical_slug,
                display_name: p.display_name,
                position: p.position,
                team: p.team,
                current_price: p.current_price,
                ownership_pct: p.ownership_pct,
                availability_pct: 100,
            }));
            const opts = { seed };
            if (mode === "auto" && poolSource === "top30" && pool.length > 30) {
                opts.limitPool = 30;
            }            if (mode === "manual") {
                opts.manualSquadIds = manualSquadIds;
                if (manualCaptainId) opts.manualCaptainId = manualCaptainId;
                opts.disableChips = disableChips;
                opts.disableWildcard = disableWildcard;
            }
            const r = await timingPoolSimulate(payload, opts);
            setResult(r);
            // Start live reveal at GW1 automatically (skippable).
            setRevealedGw(0);
            setLivePlaying(true);
            // Track: pool ids, mode, captain, total points. Squad ids used for
            // "top oblíbení hráči v simulacích" report.
            try {
                const squadIds = mode === "manual"
                    ? manualSquadIds
                    : (r?.starting_squad || r?.squad || pool.slice(0, 15).map((p) => p.fpl_id));
                track("sim_run", {
                    tool: "timing",
                    meta: {
                        mode,
                        seed,
                        total_points: r?.total_points || null,
                        player_ids: squadIds,
                        pool_ids: payload.map((p) => p.fpl_id).filter(Boolean),
                        captain_id: mode === "manual"
                            ? (manualCaptainId || null)
                            : (r?.top_captain_id || null),
                        pool_size: pool.length,
                    },
                });
            } catch { /* silent */ }
            toast.success(`Sezóna odsimulována: ${r.total_points} bodů`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Simulace selhala");
        } finally {
            setRunning(false);
        }
    };

    const rerollSeed = () => {
        setSeed(Math.floor(Math.random() * 100000));
    };

    // Live GW reveal: increments `revealedGw` by 1 every `liveSpeed` ms while
    // `livePlaying` is true and result exists. Pausable + skippable.
    const liveTimerRef = useRef(null);
    useEffect(() => {
        if (!result || !livePlaying) return;
        const totalGws = result.gw_results?.length || 0;
        if (revealedGw >= totalGws) {
            setLivePlaying(false);
            return;
        }
        liveTimerRef.current = setTimeout(() => {
            setRevealedGw((g) => Math.min(g + 1, totalGws));
        }, liveSpeed);
        return () => {
            if (liveTimerRef.current) clearTimeout(liveTimerRef.current);
        };
    }, [result, livePlaying, revealedGw, liveSpeed]);

    const skipToEnd = () => {
        setLivePlaying(false);
        if (result) setRevealedGw(result.gw_results.length);
    };

    const restartLive = () => {
        setRevealedGw(0);
        setLivePlaying(true);
    };

    const cumulativeData = result?.cumulative || [];

    return (
        <div className="space-y-4" data-testid="timing-simulator">
            <header className="border border-white/10 bg-[#0d0d0d] p-4">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <PlayCircle size={14} weight="fill" className="text-fa-yellow" />
                    <h2 className="font-display font-black text-xl uppercase tracking-tight">
                        Simulátor sezóny
                    </h2>
                    <div className="ml-auto flex items-center gap-1" data-testid="sim-mode-toggle">
                        <button
                            type="button"
                            onClick={() => setMode("auto")}
                            data-testid="sim-mode-auto"
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${mode === "auto" ? "bg-fa-yellow text-black border-fa-yellow font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                        >
                            AI Simulace
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("manual")}
                            data-testid="sim-mode-manual"
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${mode === "manual" ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                        >
                            Ruční sestava
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("manager")}
                            data-testid="sim-mode-manager"
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${mode === "manager" ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                        >
                            Manažerský
                        </button>
                    </div>
                </div>
                <p className="font-mono text-[11px] text-white/60 leading-relaxed">
                    {mode === "auto" ? (
                        <>AI z tvého poolu vybere 15-člennou soupisku <strong>v rámci £100m a max 3 hráčů z klubu</strong> a odsimuluje GW1-38 s přestupy (1 FT/GW, banka 5) a všemi 8 chipy (2× TC, BB, WC, FH).
                        <strong> Realita v cenu:</strong> každý hráč si před GW1 tajně vylosuje scénář sezóny (průměr / silný / slabý / konkrétní ročník z historie + studený či raketový start)
                        a body padají realisticky — blanky dle historické konzistence, absence, hauly i autosuby. AI scénář nezná.</>
                    ) : mode === "manual" ? (
                        <>Ty vybereš 15 hráčů (2/5/5/3) <strong>do £100m, max 3 z klubu</strong> a kapitána. Simulátor odsimuluje GW1-38 —
                        AI vybírá XI, řeší autosuby a vice-kapitána. Hráči dostávají skryté sezónní scénáře a realistické výkyvy (blanky, hauly).
                        Můžeš vypnout chipy a zamknout sestavu — odemčená sestava dělá i přestupy z celého poolu.</>
                    ) : mode === "manager" ? (
                        <>Interaktivní <strong>manažerský režim</strong>: vybereš 15 hráčů (2/5/5/3, £100m) a kapitána, sezóna se odkrývá <strong>po fázích</strong>.
                        Po každé fázi vidíš statistiky hráčů, žebříčky pozic i fiktivní tabulku PL — a máš <strong>5 přestupů</strong> + volbu kapitána na další fázi.
                        AI smí během fáze max 2 přestupy a tvého kapitána respektuje, dokud výrazně nezaostává.</>
                    ) : null}
                </p>
            </header>

            {/* Custom-30 picker (auto mode with custom30 source) */}
            {useCustom30 && (
                <Custom30Picker pool={pool} ids={custom30Ids} setIds={setCustom30Ids} />
            )}

            {/* Manual squad picker (manual + manager setup) */}
            {(mode === "manual" || (mode === "manager" && !managerReport)) && pool.length > 0 && (
                <ManualSquadPicker
                    pool={pool}
                    squadIds={manualSquadIds}
                    setSquadIds={setManualSquadIds}
                    captainId={manualCaptainId}
                    setCaptainId={setManualCaptainId}
                    disableChips={disableChips}
                    setDisableChips={setDisableChips}
                    disableWildcard={disableWildcard}
                    setDisableWildcard={setDisableWildcard}
                />
            )}

            {/* Pool status + Run controls */}
            <div className="border border-white/10 bg-[#0d0d0d] p-3 flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/45">Pool:</span>
                <span className="font-display font-black text-lg tabular-nums">{pool.length}</span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">/ {POOL_MAX_LABEL} hráčů (watchlist)</span>
                <span className="font-mono text-[10px] text-white/40">
                    {positionCounts.GKP} GKP · {positionCounts.DEF} DEF · {positionCounts.MID} MID · {positionCounts.FWD} FWD
                </span>
                {mode === "auto" && pool.length > 30 && (
                    <span className="flex items-center gap-1" data-testid="sim-pool-source">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">Zdroj:</span>
                        <button
                            type="button"
                            onClick={() => setPoolSource("top30")}
                            data-testid="sim-pool-source-top30"
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${poolSource === "top30" ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                            title="Před simulací se pool ořízne na 30 nejlepších dle projekce"
                        >
                            Top 30
                        </button>
                        <button
                            type="button"
                            onClick={() => setPoolSource("all")}
                            data-testid="sim-pool-source-all"
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${poolSource === "all" ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                            title="AI vybírá a přestupuje z celého poolu kandidátů"
                        >
                            Celý pool ({pool.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setPoolSource("custom30")}
                            data-testid="sim-pool-source-custom30"
                            className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 border ${poolSource === "custom30" ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                            title="Vyber si vlastních 30 hráčů (4 GKP / 10 DEF / 10 MID / 6 FWD), se kterými AI simuluje"
                        >
                            Vlastních 30
                        </button>
                    </span>
                )}
                {mode !== "auto" && manualSquadIds.length > 0 && !managerReport && (
                    <span className={`font-mono text-[10px] tabular-nums ${manualOverBudget ? "text-red-300 font-bold" : "text-fa-mint"}`}
                          data-testid="sim-manual-cost">
                        £{manualCost.toFixed(1)} / £100m
                    </span>
                )}
                {mode === "auto" && !validQuotaAuto && pool.length > 0 && (
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-orange-400/50 text-orange-300 bg-orange-500/[0.06]"
                          data-testid="sim-quota-warn">
                        <Warning size={10} weight="fill" className="inline mb-0.5 mr-1" />
                        Nedostatek hráčů — potřeba 2/5/5/3
                    </span>
                )}
                {useCustom30 && !custom30Valid && (
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-orange-400/50 text-orange-300 bg-orange-500/[0.06]"
                          data-testid="sim-custom30-warn">
                        <Warning size={10} weight="fill" className="inline mb-0.5 mr-1" />
                        Vlastních 30: {custom30Ids.length}/30 (4/10/10/6)
                    </span>
                )}
                {mode === "manual" && !manualValid && pool.length > 0 && (
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-orange-400/50 text-orange-300 bg-orange-500/[0.06]"
                          data-testid="sim-manual-warn">
                        <Warning size={10} weight="fill" className="inline mb-0.5 mr-1" />
                        Sestava neúplná ({manualSquadIds.length}/15)
                    </span>
                )}
                {mode !== "auto" && manualValid && manualOverBudget && (
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-red-400/50 text-red-300 bg-red-500/[0.06]"
                          data-testid="sim-budget-warn">
                        <Warning size={10} weight="fill" className="inline mb-0.5 mr-1" />
                        Nad rozpočet £100m
                    </span>
                )}
                {mode !== "auto" && manualTeamViolations.length > 0 && (
                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-red-400/50 text-red-300 bg-red-500/[0.06]"
                          data-testid="sim-team-limit-warn">
                        <Warning size={10} weight="fill" className="inline mb-0.5 mr-1" />
                        Max 3 z klubu: {manualTeamViolations.join(", ")}
                    </span>
                )}
                <span className="ml-auto flex items-center gap-2">
                    <label className="font-mono text-[10px] uppercase tracking-widest text-white/40">Seed:</label>
                    <input
                        type="number"
                        value={seed}
                        onChange={(e) => setSeed(parseInt(e.target.value, 10) || 0)}
                        data-testid="sim-seed-input"
                        className="w-20 bg-black/40 border border-white/15 px-2 py-1 font-mono text-[10px] tabular-nums focus:outline-none focus:border-fa-yellow"
                    />
                    <button
                        type="button"
                        onClick={rerollSeed}
                        data-testid="sim-reroll-seed"
                        className="font-mono text-[10px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1 hover:bg-fa-sky hover:text-black"
                    >
                        <Shuffle size={10} weight="bold" className="inline mb-0.5 mr-1" />
                        Náhoda
                    </button>
                    <button
                        type="button"
                        onClick={mode === "manager" ? startManager : runSim}
                        disabled={running || managerBusy || pool.length === 0 || !canRun || (mode === "manager" && !!managerReport)}
                        data-testid="sim-run-btn"
                        className="font-mono text-[10px] uppercase tracking-widest bg-fa-yellow text-black px-3 py-1.5 hover:bg-fa-yellowHover disabled:opacity-30 font-bold"
                    >
                        <PlayCircle size={12} weight="fill" className="inline mb-0.5 mr-1" />
                        {mode === "manager"
                            ? (managerBusy ? "Simuluji…" : managerReport ? "Sezóna běží" : "Odehrát 1. fázi (GW1–6)")
                            : running ? "Simuluji…" : result ? "Znovu simulovat" : "Odsimulovat sezónu"}
                    </button>
                </span>
            </div>

            {mode === "manager" && managerReport && (
                <ManagerPhasePanel
                    key={`${managerReport.session_id}-${managerReport.phase?.key}`}
                    report={managerReport}
                    pool={pool}
                    busy={managerBusy}
                    onNext={managerNext}
                    onReset={managerReset}
                />
            )}

            {!result && !(mode === "manager" && managerReport) && (
                <div className="border-2 border-dashed border-white/15 bg-[#0d0d0d] p-8 text-center">
                    <PlayCircle size={32} weight="regular" className="mx-auto text-white/25 mb-2" />
                    <p className="font-mono text-[11px] text-white/50">
                        {pool.length === 0
                            ? "Nejdřív sestav pool v tabu Team Season DNA."
                            : canRun
                                ? 'Stiskni "Odsimulovat sezónu" pro spuštění.'
                                : mode === "auto"
                                    ? "Pool zatím nemá plné pozice (min 2/5/5/3)."
                                    : `Vyber ${15 - manualSquadIds.length} zbývajících hráčů (2/5/5/3).`}
                    </p>
                </div>
            )}

            {result && (
                <>
                    {/* Score bar */}
                    <div className="border border-white/10 bg-[#0d0d0d] p-4">
                        <div className="flex items-baseline gap-3 flex-wrap mb-3">
                            <Trophy size={14} weight="fill" className="text-fa-yellow" />
                            <h3 className="font-display font-black text-xl uppercase tracking-tight">
                                Výsledek simulace
                            </h3>
                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 ml-auto">
                                seed {seed}
                            </span>
                        </div>
                        <div className="flex gap-2 flex-wrap" data-testid="sim-summary">
                            <StatPill
                                label="Celkem bodů"
                                value={result.total_points}
                                sub={`${result.avg_gw_points} PPG`}
                                accent="text-fa-yellow"
                            />
                            <StatPill
                                label="Vs. průměr"
                                value={result.diff_vs_avg > 0 ? `+${result.diff_vs_avg}` : `${result.diff_vs_avg}`}
                                sub={`průměr top-1M: ${result.avg_manager_points}`}
                                accent={result.diff_vs_avg > 0 ? "text-fa-mint" : "text-red-300"}
                            />
                            <StatPill
                                label="Odhad ranku"
                                value={`Top ${result.rank_estimate.top_percent}%`}
                                sub={`≥ ${result.rank_estimate.threshold} b`}
                                accent="text-fa-sky"
                            />
                            <StatPill
                                label="Best GW"
                                value={`${result.best_gw.points}`}
                                sub={`GW${result.best_gw.gw}`}
                                accent="text-fa-mint"
                            />
                            <StatPill
                                label="Worst GW"
                                value={`${result.worst_gw.points}`}
                                sub={`GW${result.worst_gw.gw}`}
                                accent="text-red-300"
                            />
                            <StatPill
                                label="Hodnota sestavy"
                                value={`£${result.squad_value}`}
                                sub={`rozpočet £${result.budget ?? 100}m`}
                                accent={result.squad_value <= (result.budget ?? 100) ? "text-fa-mint" : "text-red-300"}
                            />
                            <StatPill
                                label="Přestupy"
                                value={result.transfer_count ?? 0}
                                sub={`z poolu ${result.pool_size_used ?? "—"} hráčů`}
                                accent="text-fa-sky"
                            />
                            <StatPill
                                label="Final OR"
                                value={result.final_overall_rank ? fmtRank(result.final_overall_rank) : "—"}
                                sub="konečný rank"
                                accent="text-fa-mint"
                            />
                            <StatPill
                                label="Autosuby"
                                value={result.autosub_count ?? 0}
                                sub="náhrady z lavičky"
                                accent="text-white"
                            />
                        </div>
                    </div>

                    <ChipTimeline chips={result.chip_history} />

                    <TransfersPanel transferHistory={result.transfer_history} revealedGw={revealedGw} />

                    {result.player_scripts?.length > 0 && <ScriptsPanel scripts={result.player_scripts} />}

                    {/* Overall Rank chart — reveals gradually with the live sim */}
                    <OverallRankChart
                        cumulative={cumulativeData}
                        chipHistory={result.chip_history}
                        revealedGw={revealedGw}
                        finalOR={result.final_overall_rank}
                    />

                    {/* Cumulative chart */}
                    <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="sim-chart">
                        <div className="flex items-center gap-2 mb-2">
                            <ChartLineUp size={12} weight="fill" className="text-fa-sky" />
                            <h3 className="font-display font-black text-sm uppercase tracking-widest">
                                Kumulativní body GW1-38
                            </h3>
                            <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/40">
                                Zelená přerušovaná = průměr top-1M manažera
                            </span>
                        </div>
                        <div className="h-[320px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={cumulativeData.slice(0, revealedGw)} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                    <XAxis
                                        dataKey="gw"
                                        stroke="rgba(255,255,255,0.4)"
                                        tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                                        type="number"
                                        domain={[1, 38]}
                                        allowDecimals={false}
                                    />
                                    <YAxis
                                        stroke="rgba(255,255,255,0.4)"
                                        tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                                    />
                                    <Tooltip
                                        wrapperStyle={{ outline: "none" }}
                                        contentStyle={{
                                            background: "#0d0d0d",
                                            border: "1px solid rgba(255,255,255,0.15)",
                                            fontFamily: "IBM Plex Mono, monospace",
                                            fontSize: 11,
                                            color: "#fff",
                                        }}
                                        labelStyle={{ color: "#FFF3C2", fontWeight: 700 }}
                                        labelFormatter={(gw) => `GW ${gw}`}
                                    />
                                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }} />
                                    <ReferenceLine
                                        segment={[{ x: 1, y: 0 }, { x: 38, y: result.avg_manager_points }]}
                                        stroke="rgba(158, 232, 180, 0.5)"
                                        strokeDasharray="6 4"
                                        label={{ value: "Průměr top-1M", fill: "rgba(158, 232, 180, 0.7)", fontSize: 10, position: "insideTopRight" }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="cumulative"
                                        name="Kumulativně"
                                        stroke="#FFF3C2"
                                        strokeWidth={2.5}
                                        dot={{ r: 2 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Per-GW table */}
                    <div className="border border-white/10 bg-[#0d0d0d]" data-testid="sim-gw-table">
                        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap">
                            <Info size={11} weight="fill" className="text-fa-sky" />
                            <h3 className="font-display font-black text-sm uppercase tracking-widest">
                                Sezóna živě · GW {revealedGw}/{result.gw_results?.length || 38}
                            </h3>
                            <div className="flex items-center gap-1 ml-2" data-testid="sim-live-controls">
                                {!livePlaying && revealedGw < (result.gw_results?.length || 38) && (
                                    <button
                                        type="button"
                                        onClick={() => setLivePlaying(true)}
                                        data-testid="sim-live-play"
                                        className="font-mono text-[10px] uppercase tracking-widest text-fa-mint border border-fa-mint/40 px-2 py-1 hover:bg-fa-mint hover:text-black inline-flex items-center gap-1"
                                    >
                                        <Play size={10} weight="fill" /> Přehrát
                                    </button>
                                )}
                                {livePlaying && (
                                    <button
                                        type="button"
                                        onClick={() => setLivePlaying(false)}
                                        data-testid="sim-live-pause"
                                        className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow border border-fa-yellow/40 px-2 py-1 hover:bg-fa-yellow hover:text-black inline-flex items-center gap-1"
                                    >
                                        <Pause size={10} weight="fill" /> Pauza
                                    </button>
                                )}
                                {revealedGw < (result.gw_results?.length || 38) && (
                                    <button
                                        type="button"
                                        onClick={skipToEnd}
                                        data-testid="sim-live-skip"
                                        className="font-mono text-[10px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1 hover:bg-fa-sky hover:text-black inline-flex items-center gap-1"
                                    >
                                        <FastForward size={10} weight="fill" /> Přeskočit
                                    </button>
                                )}
                                {revealedGw >= (result.gw_results?.length || 38) && (
                                    <button
                                        type="button"
                                        onClick={restartLive}
                                        data-testid="sim-live-restart"
                                        className="font-mono text-[10px] uppercase tracking-widest text-white/60 border border-white/20 px-2 py-1 hover:border-white/50 inline-flex items-center gap-1"
                                    >
                                        <Shuffle size={10} weight="bold" /> Přehrát znovu
                                    </button>
                                )}
                                <label className="font-mono text-[9px] uppercase tracking-widest text-white/40 ml-2 flex items-center gap-1">
                                    Rychlost
                                    <select
                                        value={liveSpeed}
                                        onChange={(e) => setLiveSpeed(parseInt(e.target.value, 10))}
                                        data-testid="sim-live-speed"
                                        className="bg-black/40 border border-white/15 px-1 py-0.5 font-mono text-[10px] focus:outline-none focus:border-fa-sky"
                                    >
                                        <option value={80}>Rychle (80ms)</option>
                                        <option value={220}>Normální</option>
                                        <option value={500}>Pomalu</option>
                                        <option value={1000}>Velmi pomalu</option>
                                    </select>
                                </label>
                            </div>
                            <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-white/40">
                                Klikni na řádek pro rozbalení XI + lavičky
                            </span>
                        </div>
                        <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
                            <table className="w-full font-mono text-[10px]">
                                <thead className="sticky top-0 bg-[#0d0d0d] border-b border-white/15">
                                    <tr className="text-white/50 uppercase tracking-widest text-[9px]">
                                        <th className="text-left px-2 py-1.5">GW</th>
                                        <th className="text-left px-2 py-1.5">OR</th>
                                        <th className="text-right px-2 py-1.5">Diff</th>
                                        <th className="text-right px-2 py-1.5">%</th>
                                        <th className="text-right px-2 py-1.5">Body</th>
                                        <th className="text-right px-2 py-1.5">GW Rank</th>
                                        <th className="text-left px-2 py-1.5">Kapitán</th>
                                        <th className="text-left px-2 py-1.5">Chip</th>
                                        <th className="text-right px-2 py-1.5"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(result.gw_results || []).slice(0, revealedGw).map((r) => (
                                        <GwRow
                                            key={r.gw}
                                            r={r}
                                            expanded={expandedGw === r.gw}
                                            onToggle={(gw) => setExpandedGw(expandedGw === gw ? null : gw)}
                                        />
                                    ))}
                                    {revealedGw === 0 && (
                                        <tr>
                                            <td colSpan={9} className="px-3 py-6 text-center font-mono text-[10px] text-white/40">
                                                Sezóna se chystá… (stiskni ▶ pro spuštění)
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
